import * as React from "react"
import { useRef } from "react"

import { useMap } from "react-leaflet"
import GeoJsonLayer from "@/components/map/GeoJsonLayer"
import L from "leaflet"
import * as turf from "@turf/turf"

import usePortsGeojson from "@/hooks/usePortsGeojson"
import { ConvertDDToDM } from "@/utils/geoFormat"

export default function Ports({
  cwPortGeojson,
  setCwPortGeojson,
  voyagePlannerPoints,
  setVoyagePlannerPoints,
  toggleFlags,
  zoomLevel,
  dragged,
  resized,
  switchGisLayersActive,
}) {
  const portsEnabled = switchGisLayersActive && toggleFlags.includes("Ports")

  const map = useMap()
  // Track last clicked coordinates for adding ports
  const lastClickLatLngRef = React.useRef(null)

  // Global click handler for the Add button
  React.useEffect(() => {
    const handleDocClick = (e) => {
      if (e.target && e.target.id === "add-port-btn" && lastClickLatLngRef.current) {
        setVoyagePlannerPoints(
          turf.featureCollection([
            ...voyagePlannerPoints.features,
            turf.point([lastClickLatLngRef.current.lng, lastClickLatLngRef.current.lat]),
          ])
        )
      }
    }
    document.addEventListener("click", handleDocClick)
    return () => {
      document.removeEventListener("click", handleDocClick)
    }
  }, [voyagePlannerPoints, setVoyagePlannerPoints])
  const tooltipCountRef = useRef(0)
  function filterViewportBoundingbox(map, features) {
    const filterFeatures = features.filter((feature) => {
      return map.getBounds().contains([feature.geometry.coordinates[1], feature.geometry.coordinates[0]])
    })
    return filterFeatures
  }

  function getTonnageThreshold(zoomLevel) {
    const zoomTonnageThreshold = {
      3: 3000000,
      4: 1000000,
      5: 100000,
      6: 10000,
      7: 10000,
      8: 10000,
      9: 10000,
      10: 10000,
      11: 1000,
    }
    const minKey = Math.min(...Object.keys(zoomTonnageThreshold))
    const maxKey = Math.max(...Object.keys(zoomTonnageThreshold))
    let tonnageThreshold = zoomTonnageThreshold[zoomLevel]
    if (zoomLevel < minKey) {
      tonnageThreshold = zoomTonnageThreshold[minKey]
    } else if (zoomLevel > maxKey) {
      tonnageThreshold = 0
    }
    return tonnageThreshold
  }

  usePortsGeojson({
    enabled: portsEnabled,
    cwPortGeojson,
    setCwPortGeojson,
  })

  return !portsEnabled || cwPortGeojson === null ? null : (
    <GeoJsonLayer
      key={[zoomLevel, dragged, resized, voyagePlannerPoints.features.length]}
      data={filterViewportBoundingbox(map, cwPortGeojson.features)}
      style={{ fillColor: "black", color: "black", weight: 2 }}
      onEachFeature={(feature, layer) => {
        const tonnageThreshold = getTonnageThreshold(zoomLevel)
        if (feature.properties.tonnage >= tonnageThreshold) {
          //var divicon = L.divIcon({content:'jfjfjhfhjfjh' }); // Explicitly set to null or you will default to 12x12
          //L.marker([50.505, 30.57], {icon: divicon}).addTo(map);
          layer.bindTooltip("<div>" + feature.properties.name + "</div>", {
            permanent: true,
            direction: "center",
            opacity: 0.6,
            offset: L.point(0, -20),
            className: "my_tooltip_labels_ports", // siehe app.css und https://jsfiddle.net/maphew/gtdkxj8e/7/
          })
        }
        const isDisabled = voyagePlannerPoints.features.length >= 2
        // define button styles for enabled/disabled states to mimic MUI
        const buttonStyle = isDisabled
          ? "display: inline-block; min-width: 64px; padding: 6px 16px; border: none; border-radius: 4px; background-color: rgba(0,0,0,0.12); color: rgba(0,0,0,0.26); font-family: 'Roboto', sans-serif; font-size: 0.875rem; font-weight: 500; text-transform: none; cursor: default; box-shadow: none;"
          : "display: inline-block; min-width: 64px; padding: 6px 16px; border: none; border-radius: 4px; background-color: #004f7d; color: #fff; font-family: 'Roboto', sans-serif; font-size: 0.875rem; font-weight: 500; text-transform: none; cursor: pointer; box-shadow:0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12);"

        layer.bindPopup(
          "<div>" +
            "<strong>" +
            feature.properties.name +
            "</strong>" +
            (feature.properties.unlocode ? " (" + feature.properties.unlocode + ")" : "") +
            (feature.properties.cw_id ? "<br>" + feature.properties.cw_id : "") +
            (feature.properties.terminal ? "<br>" + feature.properties.terminal : "") +
            (feature.properties.berth ? "<br>" + feature.properties.berth : "") +
            "<br>" +
            feature.geometry.coordinates +
            "<br>" +
            ConvertDDToDM(feature.geometry.coordinates).join("&nbsp;&nbsp;&nbsp;") +
            '<br><br><button id="add-port-btn"' +
            (isDisabled ? " disabled" : "") +
            ' style="' +
            buttonStyle +
            '"' +
            ">" +
            "Add to Voyage Planner" +
            "</button>" +
            "</div>"
        )

        layer.on("click", (e) => {
          // Store the port feature's coordinates, not the click position
          lastClickLatLngRef.current = {
            lat: feature.geometry.coordinates[1],
            lng: feature.geometry.coordinates[0],
          }
          layer.openPopup(e.latlng)
        })
      }}
      pointToLayer={(feature, latlon) => {
        // Adjust the circle radius based on the current zoom level
        const radius = 500 / zoomLevel
        var featuresLayer = L.circle(latlon, {
          radius: radius,
          opacity: 1,
        })
        return featuresLayer
      }}
      interactive
      transparent
    />
  )
}
